import * as React from 'react';
import { Check, CaretUpDown } from '@phosphor-icons/react';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

export interface ComboboxItem {
  value: string;
  label: string;
  icon?: React.ReactNode;
  /** Additional data that can be passed to custom renderers */

  data?: any;
}

export interface ComboboxRenderItemProps<T extends ComboboxItem = ComboboxItem> {
  item: T;
  isSelected: boolean;
  onSelect: () => void;
}

export interface ComboboxProps<T extends ComboboxItem = ComboboxItem> {
  options: T[];
  defaultValueLabel: string;
  placeholder: string;
  noResultsLabel: string;
  className?: string;
  dropdownWidth?: number;
  value?: string;
  /** Whether the combobox is disabled */
  disabled?: boolean;
  /** Callback on open state change */
  onOpenChange?: (open: boolean) => void;
  /** Callback on value change */
  onChange?: (value: string) => void;
  /** Custom render function for each item in the dropdown */
  renderItem?: (props: ComboboxRenderItemProps<T>) => React.ReactNode;
  /** Custom render function for the selected value in the trigger button */
  renderValue?: (item: T) => React.ReactNode;
  /** Custom render function for the entire trigger button content */
  renderTrigger?: (props: { selectedItem: T | undefined; isOpen: boolean }) => React.ReactNode;
  /**
   * When false, the dropdown stays in the React tree instead of portaling to body.
   * Use inside modals/dialogs so the list is not detached from the dialog layer.
   */
  appendToBody?: boolean;
}

export function Combobox<T extends ComboboxItem = ComboboxItem>({
  options,
  defaultValueLabel,
  placeholder,
  noResultsLabel,
  className,
  onOpenChange,
  onChange,
  dropdownWidth,
  value: controlledValue,
  disabled = false,
  renderItem,
  renderValue,
  renderTrigger,
  appendToBody = true,
}: ComboboxProps<T>) {
  const [open, setOpen] = React.useState(false);
  const [internalValue, setInternalValue] = React.useState('');

  // Use controlled value if provided, otherwise use internal state
  const value = controlledValue !== undefined ? controlledValue : internalValue;
  const triggerRef = React.useRef<HTMLButtonElement>(null);
  const [triggerWidth, setTriggerWidth] = React.useState<number | null>(null);

  const selectedItem = options.find((option) => option.value === value);

  // Update width when popover opens
  const handleOpenChange = (isOpen: boolean) => {
    if (isOpen && triggerRef.current) {
      setTriggerWidth(dropdownWidth ? dropdownWidth : triggerRef.current.offsetWidth);
    }
    setOpen(isOpen);
    onOpenChange?.(isOpen);
  };

  const handleSelect = (selectedValue: string) => {
    const newValue = selectedValue === value ? '' : selectedValue;

    // Update internal state only if not controlled
    if (controlledValue === undefined) {
      setInternalValue(newValue);
    }

    setOpen(false);
    onChange?.(newValue);
  };

  // Default trigger content
  const defaultTriggerContent = selectedItem
    ? renderValue
      ? renderValue(selectedItem)
      : selectedItem.label
    : defaultValueLabel;

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          ref={triggerRef}
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn('w-full justify-between', className)}
        >
          {renderTrigger ? renderTrigger({ selectedItem, isOpen: open }) : defaultTriggerContent}
          <CaretUpDown className="opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        appendToBody={appendToBody}
        className="p-0"
        style={{
          width: triggerWidth ? `${triggerWidth}px` : 'auto',
        }}
        align="start"
      >
        <Command>
          <CommandInput placeholder={placeholder} />
          <CommandList>
            <CommandEmpty>{noResultsLabel}</CommandEmpty>
            <CommandGroup>
              {options.map((option) => {
                const isSelected = value === option.value;

                if (renderItem) {
                  return (
                    <React.Fragment key={option.value}>
                      {renderItem({
                        item: option,
                        isSelected,
                        onSelect: () => handleSelect(option.value),
                      })}
                    </React.Fragment>
                  );
                }

                return (
                  <CommandItem
                    key={option.value}
                    value={option.label}
                    onSelect={(currentLabel) => {
                      const selectedOption = options.find((opt) => opt.label === currentLabel);

                      handleSelect(selectedOption?.value || '');
                    }}
                  >
                    {option.label}
                    <Check className={cn('ml-auto', isSelected ? 'opacity-100' : 'opacity-0')} />
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
